/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useDispatch, useSelector } from 'react-redux';
import ChevronDown from '../../assets/ChevronDown';
import ErrorIcon from '../../assets/ErrorIcon';
import SearchIcon from '../../assets/SearchIcon';
import useScreenLayout from '../../hooks/useScreenLayout';
import { actions as searchActions } from '../../reducers/searchReducer';
import type { RootState } from '../../store';
import type { DataWorldChartTypes } from '../../types/types';
import { getCookie, setCookie } from '../../utils/cookies';
import { handleOptionsSorting } from '../../utils/countries-sort';
import { displayToast } from '../../utils/displayToast';
import { SearchInputType } from '../../utils/string';
import { trackCustomEvent } from '../../utils/trackCustomEvents';
import OutsideClickHandler from '../core/outsideclickhandler';

interface SearchForRecordsProps {
  defaultValues?: {
    firstName?: string;
    lastName?: string;
    country?: string;
  };
  countries: DataWorldChartTypes[];
  userCountry?: string;
}

export default function SearchForRecords({
  defaultValues,
  countries,
  userCountry,
}: SearchForRecordsProps) {
  const { isTabletLayout } = useScreenLayout();
  const dispatch = useDispatch();
  const router = useRouter();

  const firstNameInput = React.useRef<HTMLInputElement>(null);
  const lastNameInput = React.useRef<HTMLInputElement>(null);
  const countryInput = React.useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState<string>(
    defaultValues?.firstName || ''
  );
  const [lastName, setLastName] = useState<string>(
    defaultValues?.lastName || ''
  );
  const [searchedCountry, setSearchedCountry] = useState<string>(
    defaultValues?.country || ''
  );

  const [countriesOptions, setCountriesOptions] = useState<
    readonly any[] | undefined
  >(undefined);
  const [openCountryDropdown, setOpenCountryDropdown] = useState(false);
  const [selectedCountryIso, setSelectedCountryIso] = useState<string>('');

  const selectedCountry = useMemo(() => {
    return countriesOptions?.find(
      (country: DataWorldChartTypes) => country?.iso_2 === selectedCountryIso
    );
  }, [selectedCountryIso]);

  const handleFirstNameSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFirstName(value);
  };

  const handleLastNameSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLastName(value);
  };

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchedCountry(value);
  };

  const handleSelectedInput = (input: SearchInputType) => {
    if (input === SearchInputType.FIRST_NAME) {
      firstNameInput.current?.focus();
      putFirstNameInFocus();
    } else if (input === SearchInputType.LAST_NAME) {
      lastNameInput.current?.focus();
      putLastNameInFocus();
    }
  };

  const handleSelectCountry = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    handleSelectedInput(SearchInputType.COUNTRY);
    setOpenCountryDropdown(true);
  };

  const [firstNameInFocus, setFirstNameInFocus] = useState(false);
  const [lastNameInFocus, setLastNameInFocus] = useState(false);

  useEffect(() => {
    const optionsFromPropsOrRedux = (countries as DataWorldChartTypes[])?.map(
      (country: DataWorldChartTypes) => ({
        value: country.iso_2,
        label: country.name,
        ...country,
      })
    );
    const newOptions = handleOptionsSorting(
      optionsFromPropsOrRedux,
      mostVisitedCountriesIds,
      userCountry
    );
    if (optionsFromPropsOrRedux?.length > 0 && newOptions?.length > 0) {
      const newOptionsWithCorrectIndexes = newOptions
        .map((option: any, index: number) => ({
          ...option,
          index: index,
        }))
        .filter((option: any) => option?.name?.toLowerCase() !== 'world');
      setCountriesOptions(newOptionsWithCorrectIndexes);
      setSelectedCountryIso(newOptionsWithCorrectIndexes?.[0]?.iso_2);
    }
  }, [countries, router?.pathname, userCountry]);

  const putFirstNameInFocus = () => {
    setFirstNameInFocus(true);
    setLastNameInFocus(false);
  };

  const putLastNameInFocus = () => {
    setFirstNameInFocus(false);
    setLastNameInFocus(true);
  };

  const putFirstNameOutOfFocus = () => {
    setFirstNameInFocus(false);
  };

  const putLastNameOutOfFocus = () => {
    setLastNameInFocus(false);
  };

  const mostVisitedCountriesIds = getCookie('mostVisitedCountriesIds');
  const recentSearches = getCookie('recentSearches');
  const { defaultNrOfResults } = useSelector((state: RootState) => state.user);
  const { redirectedFrom } = useSelector((state: RootState) => state.search);

  const handleKeyDownSearch = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    if (
      firstName?.length === 0 &&
      lastName?.length === 0 &&
      selectedCountryIso?.length === 0
    ) {
      displayToast(t('please-select'), 'error', <ErrorIcon />);
      return;
    }

    if (mostVisitedCountriesIds) {
      if (selectedCountry) {
        const newMostVisitedCountriesIds = [
          ...JSON.parse(mostVisitedCountriesIds as string),
        ];

        newMostVisitedCountriesIds.unshift(selectedCountry.ID);

        const filteredMostVisitedCountriesIds = [
          ...new Set(newMostVisitedCountriesIds),
        ];

        if (newMostVisitedCountriesIds?.length < 4) {
          setCookie(
            'mostVisitedCountriesIds',
            JSON.stringify(filteredMostVisitedCountriesIds)
          );
        } else {
          setCookie(
            'mostVisitedCountriesIds',
            JSON.stringify(filteredMostVisitedCountriesIds.slice(0, 3))
          );
        }
      }
    } else {
      setCookie(
        'mostVisitedCountriesIds',
        JSON.stringify([(selectedCountry as DataWorldChartTypes)?.ID])
      );
    }

    if (selectedCountry && lastName?.length > 0 && firstName?.length > 0) {
      dispatch(
        searchActions.setIsLoadingSearchResults({
          isLoadingSearchResults: true,
        })
      );

      dispatch(
        searchActions.setFirstName({
          first_name: firstName,
        })
      );

      dispatch(
        searchActions.setLastName({
          last_name: lastName,
        })
      );

      router.push({
        pathname: '/search',
        query: {
          firstName,
          lastName,
          country: selectedCountry?.iso_2,
          page: 1,
          pageSize: defaultNrOfResults,
        },
      });

      trackCustomEvent(
        'Search from search bar',
        `${firstName} ${lastName} - ${selectedCountry?.name}`
      );

      if (recentSearches) {
        const search = {
          country: selectedCountry?.name,
          firstName,
          lastName,
        };

        const newRecentSearches = [...JSON.parse(recentSearches as string)];

        newRecentSearches.unshift(search);

        const filteredRecentSearches = newRecentSearches.filter(
          (search, index, self) =>
            index ===
            self.findIndex(
              (t) =>
                t.country === search.country &&
                t.firstName === search.firstName &&
                t.lastName === search.lastName
            )
        );

        if (filteredRecentSearches?.length <= 5) {
          setCookie('recentSearches', JSON.stringify(filteredRecentSearches));
        } else {
          const newfilteredRecentSearches = filteredRecentSearches?.slice(0, 5);

          setCookie(
            'recentSearches',
            JSON.stringify(newfilteredRecentSearches)
          );
        }
      } else {
        const search = {
          country: selectedCountry?.name,
          firstName,
          lastName,
        };

        setCookie('recentSearches', JSON.stringify([search]));
      }
    }

    if (
      !selectedCountry ||
      lastName?.length < 1 ||
      !lastName ||
      firstName?.length < 1 ||
      !firstName
    ) {
      displayToast(t('please-select'), 'error', <ErrorIcon />);
    }

    dispatch(
      searchActions.setIsSearchTriggeredWithFilters({
        isSearchTriggeredWithFilters: false,
      })
    );
  };

  const { t } = useTranslation(['navbar', 'new-navbar']);

  const getFilteredCountries = useMemo(() => {
    return countriesOptions?.filter((country) => {
      return country?.name
        ?.toLowerCase()
        .includes(searchedCountry?.toLowerCase());
    });
  }, [countriesOptions, searchedCountry]);

  const handleSelectCountryFromDropdown =
    (selectedCountry: DataWorldChartTypes) =>
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setSelectedCountryIso(selectedCountry.iso_2);
      setOpenCountryDropdown(false);
      setSearchedCountry('');
    };

  useEffect(() => {
    if (redirectedFrom) {
      const foundCountry = getFilteredCountries?.find(
        (country) => country?.label?.toLowerCase() === redirectedFrom
      );
      foundCountry && setSelectedCountryIso(foundCountry.iso_2);
    }
  }, [getFilteredCountries]);

  return (
    <div className="flex flex-col justify-between tablet:flex-row border-lg">
      <button
        onClick={() => handleSelectedInput(SearchInputType.FIRST_NAME)}
        className="relative flex-1 rounded-t-lg border-[1px] border-[#CFD1D4] tablet:rounded-l-lg tablet:rounded-tr-none"
      >
        <p
          className={`absolute left-[16px] z-[13] pt-[10px] text-[12px] font-semibold cursor-text ${
            firstNameInFocus ? 'text-[#257C6B]' : 'text-[#6F757E]'
          }`}
        >
          {t('first-name')}
        </p>
        <div className={`relative ${firstNameInFocus ? 'z-[11]' : 'z-[1]'}`}>
          <OutsideClickHandler onOutsideClick={putFirstNameOutOfFocus}>
            <input
              value={firstName}
              onChange={handleFirstNameSearch}
              onBlur={putFirstNameOutOfFocus}
              type={'text'}
              ref={firstNameInput}
              className={`z-[10] border-none rounded-[8px] pt-8
              placeholder-shown:pl-[16px] pl-[16px] pb-[16px] font-medium w-full
              focus:border-transparent focus:outline-none focus:shadow-[0_0_0_0_0]
              ${isTabletLayout ? 'text-[14px]' : ''}`}
              placeholder={`Enter ${t('first-name')}`}
              autoComplete="off"
            />
          </OutsideClickHandler>{' '}
        </div>
      </button>
      <button
        onClick={() => handleSelectedInput(SearchInputType.LAST_NAME)}
        className="relative flex-1 border-[1px] border-[#CFD1D4]"
      >
        <p
          className={`absolute left-[16px] z-[13] pt-[10px] text-[12px] font-semibold cursor-text ${
            lastNameInFocus ? 'text-[#257C6B]' : 'text-[#6F757E]'
          }`}
        >
          {t('last-name')}
        </p>
        <div className={`relative ${lastNameInFocus ? 'z-[11]' : 'z-[1]'}`}>
          <OutsideClickHandler onOutsideClick={putLastNameOutOfFocus}>
            <input
              value={lastName}
              onChange={handleLastNameSearch}
              onBlur={putLastNameOutOfFocus}
              type={'text'}
              ref={lastNameInput}
              className={`z-[10] border-none rounded-[8px] pt-8
              placeholder-shown:pl-[16px] pl-[16px] pb-[16px] font-medium w-full
              focus:border-transparent focus:outline-none focus:shadow-[0_0_0_0_0]
              ${isTabletLayout ? 'text-[14px]' : ''}`}
              placeholder={`Enter ${t('last-name')}`}
              autoComplete="off"
            />
          </OutsideClickHandler>{' '}
        </div>
      </button>
      <button
        onClick={handleSelectCountry}
        onFocus={() => handleSelectedInput(SearchInputType.COUNTRY)}
        className="relative flex-1 border-[1px] border-[#CFD1D4]"
      >
        <p
          className={`absolute left-[16px] z-[13] pt-[10px] text-[12px] font-semibold cursor-pointer ${
            openCountryDropdown ? 'text-[#257C6B]' : 'text-[#6F757E]'
          }`}
        >
          {t('country')}
        </p>
        <OutsideClickHandler
          onOutsideClick={() => setOpenCountryDropdown(false)}
          styles="flex flex-row py-[24px] pt-8 pb-[16px] px-[16px] w-full"
        >
          <p className={`text-[16px] font-medium w-fit`}>
            {selectedCountry?.name}
          </p>
          <ChevronDown className={`cursor-pointer`} />
          <div
            className={`${
              openCountryDropdown
                ? 'flex flex-col absolute tablet:top-[calc(100%+4px)] top-full left-0 w-full h-[265px] overflow-auto z-[6] bg-white border rounded py-[15px]'
                : ''
            }`}
          >
            {openCountryDropdown ? (
              <div className="group relative mx-[15px] mb-[8px] w-[calc(100%-32px)] min-w-[250px] rounded border">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchedCountry}
                  ref={countryInput}
                  className="p-[10px_44px_10px_10px] w-full h-[40px] text-[15px] text-left bg-transparent
                  border-transparent group-hover:border-[#E7E8E9] focus:border-transparent
                  focus:outline-none focus:shadow-[0_0_0_0_0]
                  placeholder-[#CFD1D4] group-hover:placeholder-[#9FA3A9] focus:placeholder-[#CFD1D4]"
                  onChange={handleCountrySearch}
                />
                <div className="flex absolute top-[2px] right-[10px] z-[1] justify-center items-center h-full">
                  {searchedCountry ? (
                    <button
                      className="p-2 text-2xl text-[#CFD1D4] cursor-pointer"
                      onClick={() => setSearchedCountry('')}
                    >
                      &times;
                    </button>
                  ) : (
                    <SearchIcon
                      height={24}
                      width={24}
                      fill="#CFD1D4"
                      onClick={() => countryInput.current?.focus()}
                      className="text-[#9FA3A9] group-hover:text-[#6F757E]"
                    />
                  )}
                </div>
              </div>
            ) : null}
            {openCountryDropdown
              ? getFilteredCountries?.map(
                  (country: DataWorldChartTypes, index: number) => {
                    return (
                      <button
                        key={country?.iso_3 || index}
                        onClick={handleSelectCountryFromDropdown(country)}
                        className={`flex items-center py-[8px] px-[16px] hover:bg-[#E7E8E9]`}
                      >
                        <ReactCountryFlag
                          countryCode={country.iso_2}
                          svg
                          alt={`${country?.name} flag`}
                          cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                          cdnSuffix="svg"
                          style={{
                            borderRadius: '9999px',
                            width: '24x',
                            height: '24px',
                          }}
                        />
                        <p className="ml-[12px] text-[15px] text-left">
                          {country?.name}
                        </p>
                      </button>
                    );
                  }
                )
              : null}
          </div>
        </OutsideClickHandler>
      </button>
      <div className="flex justify-center bg-[#409F85] rounded-b-lg border-[1px] border-[#409F85] tablet:rounded-r-lg tablet:rounded-bl-none">
        <button
          type="button"
          onClick={handleSearch}
          onKeyDown={handleKeyDownSearch}
          aria-label="Search"
          id="search-button"
          className="flex flex-row justify-center items-center py-[22px] px-[24px] text-[16px] font-semibold text-white"
        >
          Search{' '}
          <SearchIcon
            height={24}
            width={24}
            fill="white"
            className="ml-[8px]"
          />
        </button>
      </div>
    </div>
  );
}
